import { ISiteDetails } from "../models/Interfaces";
import { isSiteAiImageConnected } from "./siteAi";

const OPENAI_IMAGE_ENDPOINT = "https://api.openai.com/v1";
const AZURE_IMAGE_API_VERSION = "2025-04-01-preview";

export interface IAiImageResult {
  ok: boolean;
  blob: Blob | null;
  error: string | null;
}

const providerKey = (site?: ISiteDetails | null): string => (site?.AIProvider ?? "").trim().toLowerCase();

// These providers can take the OpenAI-style image calls this client knows how to send.
const imageProviders = new Set(["openai", "azure-openai", "xai", "custom"]);

export const canGenerateAiImage = (site?: ISiteDetails | null): boolean =>
  isSiteAiImageConnected(site) && imageProviders.has(providerKey(site));

export const canEditAiImage = (site?: ISiteDetails | null): boolean =>
  canGenerateAiImage(site);

const endpointBase = (site: ISiteDetails): string => {
  const raw = (site.AIEndpoint ?? "").trim().replace(/\/$/, "");
  if (providerKey(site) === "openai") {
    return raw || OPENAI_IMAGE_ENDPOINT;
  }

  return raw;
};

const isGptImageModel = (model: string): boolean => model.toLowerCase().includes("gpt-image");

const authHeaders = (site: ISiteDetails): Record<string, string> => {
  if (providerKey(site) === "azure-openai") {
    return { "api-key": site.AIApiKey ?? "" };
  }

  return { Authorization: `Bearer ${site.AIApiKey ?? ""}` };
};

const imageUrl = (site: ISiteDetails, action: "generations" | "edits"): string => {
  const base = endpointBase(site);
  if (providerKey(site) !== "azure-openai") {
    return `${base}/images/${action}`;
  }

  if (/\/images\/(generations|edits)/i.test(base)) {
    return base;
  }

  if (/\/openai\/v1$/i.test(base)) {
    return `${base}/images/${action}`;
  }

  const root = base.replace(/\/$/, "");
  const model = encodeURIComponent((site.AIImageModel ?? "").trim());
  return `${root}/openai/deployments/${model}/images/${action}?api-version=${AZURE_IMAGE_API_VERSION}`;
};

const failed = (error: string): IAiImageResult => ({ ok: false, blob: null, error });

const sniffImageType = (bytes: Uint8Array): string => {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }

  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "image/png";
  }

  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return "image/gif";
  }

  if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[8] === 0x57 && bytes[9] === 0x45) {
    return "image/webp";
  }

  return "image/png";
};

const blobFromBase64 = (b64: string): Blob => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: sniffImageType(bytes) });
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

const asPng = async (blob: Blob): Promise<Blob> => {
  if (blob.type === "image/png") {
    return blob;
  }

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
  bitmap.close();

  const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  return png ?? blob;
};

const providerMessage = (data: any, fallback: string): string => {
  const message = data?.error?.message || data?.message || data?.error;
  if (typeof message === "string" && message.trim()) {
    return message.trim();
  }

  return fallback;
};

const readImageResponse = async (response: Response): Promise<IAiImageResult> => {
  const text = await response.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    return failed(providerMessage(data, text || `Image request failed (${response.status})`));
  }

  const image = data?.data?.[0];
  if (image?.b64_json) {
    return { ok: true, blob: blobFromBase64(image.b64_json), error: null };
  }

  if (image?.url) {
    const downloaded = await fetch(image.url);
    if (!downloaded.ok) {
      return failed("The provider returned an image URL that could not be downloaded.");
    }

    const bytes = new Uint8Array(await downloaded.arrayBuffer());
    return { ok: true, blob: new Blob([bytes], { type: sniffImageType(bytes) }), error: null };
  }

  return failed(providerMessage(data, "The provider did not return an image."));
};

const generationBody = (site: ISiteDetails, prompt: string): Record<string, unknown> => {
  const model = (site.AIImageModel ?? "").trim();
  const provider = providerKey(site);
  const body: Record<string, unknown> = { model, prompt, n: 1 };

  if (provider === "xai") {
    body.response_format = "b64_json";
    return body;
  }

  body.size = "1024x1024";
  // GPT image models reject the legacy response_format field and return base64 themselves.
  if (provider === "azure-openai" || isGptImageModel(model)) {
    body.output_format = "png";
  } else {
    body.response_format = "b64_json";
  }

  return body;
};

export const generateAiImage = async (site: ISiteDetails, prompt: string): Promise<IAiImageResult> => {
  if (!canGenerateAiImage(site)) {
    return failed("Image generation is not available for this site.");
  }

  try {
    const response = await fetch(imageUrl(site, "generations"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(site)
      },
      body: JSON.stringify(generationBody(site, prompt))
    });

    return await readImageResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : "The image request could not be sent.";
    return failed(message);
  }
};

export const editAiImage = async (site: ISiteDetails, prompt: string, source: Blob): Promise<IAiImageResult> => {
  if (!canEditAiImage(site)) {
    return failed("Image editing is not available for this site.");
  }

  const model = (site.AIImageModel ?? "").trim();
  const provider = providerKey(site);

  try {
    if (provider === "xai") {
      const mime = source.type || "image/png";
      const b64 = await blobToBase64(source);
      const response = await fetch(imageUrl(site, "edits"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(site)
        },
        body: JSON.stringify({
          model,
          prompt,
          response_format: "b64_json",
          image: {
            url: `data:${mime};base64,${b64}`,
            type: "image_url"
          }
        })
      });

      return await readImageResponse(response);
    }

    const png = await asPng(source);
    const file = new File([png], "source.png", { type: "image/png" });
    const form = new FormData();
    form.append("model", model);
    form.append("prompt", prompt);

    if (provider === "azure-openai" || isGptImageModel(model)) {
      form.append("image[]", file);
      form.append("output_format", "png");
    } else {
      form.append("image", file);
      form.append("n", "1");
      form.append("size", "1024x1024");
      form.append("response_format", "b64_json");
    }

    const response = await fetch(imageUrl(site, "edits"), {
      method: "POST",
      headers: authHeaders(site),
      body: form
    });

    return await readImageResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : "The image request could not be sent.";
    return failed(message);
  }
};
